/**
 * Comment la feuille met SES outils dans la trousse.
 *
 * La trousse est rendue UNE SEULE FOIS, au niveau de l'écran (deux trousses se superposeraient). La
 * feuille, elle, vit dans le dock — elle publie donc ses outils (tableau à dresser, arbre, tableau
 * d'avancement, corrigé) dans ce contexte, et la trousse les lit.
 *
 * Sans provider le contexte renvoie une liste vide : tout écran qui n'a pas de feuille marche comme
 * avant.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { TrousseItem } from './trousse-items';

interface Ctx {
  tools: TrousseItem[];
  publish: (owner: string, tools: TrousseItem[]) => void;
}

const TrousseToolsCtx = createContext<Ctx | null>(null);

export function TrousseToolsProvider({ children }: { children: ReactNode }) {
  const [byOwner, setByOwner] = useState<Record<string, TrousseItem[]>>({});
  const publish = useCallback((owner: string, tools: TrousseItem[]) => {
    setByOwner((prev) => {
      const had = prev[owner];
      if (!tools.length && !had) return prev;
      if (!tools.length) {
        const next = { ...prev };
        delete next[owner];
        return next;
      }
      return { ...prev, [owner]: tools };
    });
  }, []);
  const tools = useMemo(() => Object.values(byOwner).flat(), [byOwner]);
  const value = useMemo(() => ({ tools, publish }), [tools, publish]);
  return <TrousseToolsCtx.Provider value={value}>{children}</TrousseToolsCtx.Provider>;
}

/** Ce que la trousse ajoute à sa liste. Vide hors d'un provider. */
export function useTrousseTools(): TrousseItem[] {
  return useContext(TrousseToolsCtx)?.tools ?? [];
}

/**
 * La feuille publie ses outils. `owner` distingue les publieurs (la feuille, un bloc) ; la liste est
 * retirée au démontage. `deps` dit quand elle change — comme un `useEffect`, pour ne pas republier
 * une liste identique à chaque rendu.
 */
export function useTrousseToolsPublisher(
  owner: string,
  build: () => TrousseItem[],
  deps: unknown[],
): void {
  const ctx = useContext(TrousseToolsCtx);
  const latest = useRef(build);
  latest.current = build;
  // ⚠️ `ctx` ne doit JAMAIS être une dépendance de l'effet : publier change la liste du contexte,
  // donc son identité, donc l'effet se relancerait et republierait — une boucle sans fin (elle ne
  // se voyait pas tant qu'un seul publieur existait et qu'il publiait une liste vide, cas où
  // `publish` ne touche pas l'état). Seule compte la PRÉSENCE d'un provider.
  const publish = useRef(ctx?.publish);
  publish.current = ctx?.publish;
  const inside = !!ctx;
  useEffect(() => {
    const pub = publish.current;
    if (!pub) return;
    pub(owner, latest.current());
    return () => pub(owner, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inside, owner, ...deps]);
}
